import React, { Component, ErrorInfo, ReactNode } from 'react';

import { AlertTriangleIcon, RefreshCwIcon } from 'lucide-react';
import { Box, Stack, Typography } from '@mui/material';
import { Button } from '@repo/ui';

import { IconBadge } from 'components/UI/IconBadge';

interface AuthErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback. Defaults to a compact "something went wrong" card. */
  fallback?: ReactNode;
}

interface AuthErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Scoped error boundary for the auth surfaces (sign in, password setup,
 * reset, etc). Renders a friendly fallback instead of a blank/broken screen
 * if something throws while rendering the auth flow. Not a full-page
 * boundary (see apps/platform ErrorBoundary for that shape) — this one is
 * sized to sit inside the sign-in card.
 */
export class AuthErrorBoundary extends Component<AuthErrorBoundaryProps, AuthErrorBoundaryState> {
  public state: AuthErrorBoundaryState = { hasError: false, error: null };

  public static getDerivedStateFromError(error: Error): AuthErrorBoundaryState {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // No PII (email/code) is available at this boundary; safe to log.
    console.error('[AuthErrorBoundary] Auth flow crashed:', error, errorInfo.componentStack);
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box className='flex flex-col items-center justify-center pt-[20vh] pb-8 px-4 w-full'>
          <Stack spacing={3} alignItems='center' textAlign='center' sx={{ maxWidth: 360 }}>
            {/*
              The shared badge (§36). Was a byte-identical copy of the disc in
              `components/UI/ErrorBoundary` — same 56px, same
              `rgba(244,67,54,.1)` fill, same hardcoded `#f44336` glyph. Both
              now render the one grey badge, toned by the palette's error
              colour rather than a literal hex.
            */}
            <IconBadge icon={AlertTriangleIcon} tone='alert' />
            <Stack spacing={1}>
              <Typography variant='h6'>Something went wrong</Typography>
              <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.5 }}>
                We hit an unexpected error loading sign in. Please try again. If it keeps
                happening, contact support.
              </Typography>
            </Stack>
            <Button variant='primary' onClick={this.handleRetry} sx={{ mt: 1 }}>
              <RefreshCwIcon size={16} className='mr-2' />
              Try again
            </Button>
          </Stack>
        </Box>
      );
    }

    return this.props.children;
  }
}
